import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from './cn';

export interface EmptyStateProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  'title'
> {
  icon?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  /** Primary and secondary actions, e.g. a primary `Button` and a ghost one. */
  actions?: ReactNode;
  /** `dashed` frames the state as a drop target or placeholder. */
  variant?: 'plain' | 'dashed';
}

/** Centered placeholder that names what is missing and the next step. */
export const EmptyState = ({
  icon,
  title,
  description,
  actions,
  variant = 'plain',
  className,
  ...props
}: EmptyStateProps) => (
  <div
    className={cn(
      'flex flex-col items-center justify-center px-6 py-10 text-center',
      variant === 'dashed' &&
        'rounded-xl border border-dashed border-border-strong',
      className
    )}
    {...props}
  >
    {icon != null && (
      <div
        aria-hidden="true"
        className="mb-4 flex size-10 items-center justify-center rounded-lg border border-border bg-surface-sunken text-fg-muted shadow-sm [&_svg]:size-5"
      >
        {icon}
      </div>
    )}
    <p className="text-sm font-semibold text-fg">{title}</p>
    {description != null && (
      <p className="mt-1 max-w-sm text-sm text-fg-muted">{description}</p>
    )}
    {actions != null && (
      <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
        {actions}
      </div>
    )}
  </div>
);
